/**
 * Cytoscape Configuration and Styling
 * Defines node/edge styles, colors, and layout options
 */

export const cytoscapeStyles = [
  // Default node style - use degree for sizing
  {
    selector: 'node',
    style: {
      'background-color': '#999',
      'label': '',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 7,
      'color': '#000',
      'width': 'mapData(degree, 0, 100, 10, 30)',
      'height': 'mapData(degree, 0, 100, 10, 30)',
      'padding': 2,
      'text-opacity': 0,
      'border-width': 0,
    },
  },
  // Planner - Red
  {
    selector: 'node[type="Planner"]',
    style: {
      'background-color': '#FF6B6B',
    },
  },
  // Person - Teal
  {
    selector: 'node[type="Person"]',
    style: {
      'background-color': '#4ECDC4',
    },
  },
  // PlanningClass - Light Blue
  {
    selector: 'node[type="PlanningClass"]',
    style: {
      'background-color': '#45B7D1',
    },
  },
  // PlanningType - Gold
  {
    selector: 'node[type="PlanningType"]',
    style: {
      'background-color': '#F7B731',
    },
  },
  // PlanningLanguage - Purple
  {
    selector: 'node[type="PlanningLanguage"]',
    style: {
      'background-color': '#5F27CD',
    },
  },
  // ImplementationLanguage - Cyan
  {
    selector: 'node[type="ImplementationLanguage"]',
    style: {
      'background-color': '#00D2D3',
    },    
  },
  {
    selector: 'node[type="Year"]',
    style: {
      'background-color': '#9AE6B4',
      'width': 14,
      'height': 14,
      'label': 'data(label)',
      'text-valign': 'bottom',
      'text-halign': 'center',
      'font-size': 8,
    },
  },
  // Nodes with visible labels (in subgraph view)
  {
    selector: 'node.show-label',
    style: {
      'label': 'data(label)',
      'text-opacity': 1,
      'color': '#000000ff',
      'font-size': 6,
      'font-weight': 'normal',
      'text-background-opacity': 0,
      'text-outline-width': 0,
      'text-valign': 'bottom',
      'text-halign': 'right',
      'text-margin-y': 0,
      'text-margin-x': 0,
    },
  },
  // Selected node
  {
    selector: 'node:selected',
    style: {
      'border-width': 3,
      'border-color': '#333',
      'label': 'data(label)',
      'text-opacity': 1,
      'width': 'mapData(degree, 0, 100, 20, 40)',
      'height': 'mapData(degree, 0, 100, 20, 40)',
      'color': '#000',
      'font-size': 8,
    },
  },
  {
    selector: 'edge',
    style: {
      'line-color': '#eee',
      'target-arrow-color': '#eee',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'label': '',
      'font-size': 6,
      'text-background-color': '#fff',
      'text-background-opacity': 0.9,
      'text-background-padding': 1,
      'opacity': 0.3,
      'width': 0.5,
    },
  },
  {
    selector: 'edge[type="CONTRIBUTED_TO"]',
    style: {
      'line-color': '#FF69B4',
      'target-arrow-color': '#FF69B4',
      'width': 0.6,
      'opacity': 0.35,
    },
  },
  {
    selector: 'edge[type="SUPPORTS_PLANNING_CLASS"]',
    style: {
      'line-color': '#45B7D1',
      'target-arrow-color': '#45B7D1',
      'width': 0.5,
      'opacity': 0.3,
    },
  },
  {
    selector: 'edge[type="PROVIDES_PLANNING_FUNCTIONALITY"]',
    style: {
      'line-color': '#F7B731',
      'target-arrow-color': '#F7B731',
      'width': 0.5,
      'opacity': 0.3,
    },
  },
  {
    selector: 'edge[type="SUPPORTS_PLANNING_LANGUAGE"]',
    style: {
      'line-color': '#5F27CD',
      'target-arrow-color': '#5F27CD',
      'width': 0.5,
      'opacity': 0.3,
    },
  },
  {
    selector: 'edge[type="IMPLEMENTED_IN"]',
    style: {
      'line-color': '#00D2D3',
      'target-arrow-color': '#00D2D3',
      'width': 0.5,
      'opacity': 0.3,
    },
  },
  {
    selector: 'edge[type="PUBLISHED_IN"]',
    style: {
      // Make publication edges darker and more visible
      'line-color': '#16ff06',
      'target-arrow-color': '#16ff06',
      'width': 0.5,
      'opacity': 0.95,
      'z-index': 10,
      'curve-style': 'bezier',
      'target-arrow-shape': 'triangle',
    },
  },
];

/**
 * Edge-Weighted Spring Embedded Layout Configuration
 * Using COSE layout with spring-like parameters
 * Optimized to spread nodes and reduce center clutter
 */
export const cytoscapeLayout = {
  name: 'cose',
  directed: true,
  
  // Core spacing parameters - increased to spread out graph
  nodeRepulsion: 25000,     // Much stronger repulsion to push nodes apart and away from center
  idealEdgeLength: 200,     // Longer edges to spread nodes further
  edgeElasticity: 200,      // Stiffer springs to maintain distance
  gravity: 0.1,             // Slight gravity to prevent drift but not clumping

  // Performance & quality
  numIter: 3000,            // More iterations = better layout
  initialTemp: 400,
  coolingFactor: 0.96,
  minTemp: 0.5,

  // Misc
  animate: false,           // Set to false for faster computation
  fit: false,
  padding: 50,
};

/**
 * Force-directed layout for subgraphs
 * Balanced parameters for proper node spacing in smaller graphs
 */
export const cytoscapeSubgraphLayout = {
  name: 'cose',
  directed: true,
  
  // Balanced spacing for subgraphs - proper distances between nodes
  nodeRepulsion: 5000,      // Moderate repulsion for consistent spacing
  idealEdgeLength: 80,      // Reasonable edge length for subgraph
  edgeElasticity: 100,      // Medium elasticity for stability
  gravity: 0.25,            // Stronger gravity to keep subgraph compact

  // Performance & quality
  numIter: 2000,            // Enough iterations for good convergence
  initialTemp: 300,
  coolingFactor: 0.97,
  minTemp: 1.0,

  // Misc
  animate: false,           // Set to false for faster computation
  fit: false,
  padding: 30,
};

/**
 * Color map for node types
 */
export const nodeTypeColors = {
  Planner: '#FF6B6B',
  Person: '#4ECDC4',
  PlanningClass: '#45B7D1',
  PlanningType: '#F7B731',
  PlanningLanguage: '#5F27CD',
  ImplementationLanguage: '#00D2D3',
  Year: '#0cf65eff',
};

/**
 * Color map for edge types
 */
export const edgeTypeColors = {
  CONTRIBUTED_TO: '#FF69B4',
  SUPPORTS_PLANNING_CLASS: '#0015ffff',
  PROVIDES_PLANNING_FUNCTIONALITY: '#F7B731',
  SUPPORTS_PLANNING_LANGUAGE: '#5F27CD',
  IMPLEMENTED_IN: '#00D2D3',
  PUBLISHED_IN: '#16ff06ff',
};

export const nodeTypeLabels = {
Planner: 'Planner',
Person: 'Person',
PlanningClass: 'Planning Class',
PlanningType: 'Planning Type',
PlanningLanguage: 'Planning Language',
ImplementationLanguage: 'Implementation Language',
Year: 'Year',
};